import { FormEvent, useEffect, useState } from "react";
import {
  useRoles,
  useRole,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useUpdateRolePermissions,
  useAvailablePermissions,
} from "@/hooks/useRoles";
import { usePermissions } from "@/hooks/usePermissions";
import { AdminRole } from "@/types/rbac";
import { PageHeader, Button } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

interface RoleFormState {
  name: string;
  slug: string;
  description: string;
}

const emptyForm: RoleFormState = {
  name: "",
  slug: "",
  description: "",
};

const RESOURCE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  content_library: "Content Library",
  help_center: "Help Center",
  newsroom: "Newsroom",
  authors: "Authors",
  media_library: "Media Library",
  models_settings: "Model Settings",
  tools_settings: "Tool Settings",
  widget_settings: "Widget Settings",
  users_management: "User Management",
  roles_management: "Role Management",
  chat_playground: "Chat Playground",
};

const RolesManagement = () => {
  const { data: roles, isLoading } = useRoles();
  const { data: availablePermissions } = useAvailablePermissions();
  const { canCreate, canEdit, canDelete, isSuperadmin } = usePermissions();

  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();
  const updatePermissionsMutation = useUpdateRolePermissions();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [form, setForm] = useState<RoleFormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [permissionsRoleId, setPermissionsRoleId] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  // Fetch role details when editing permissions
  const { data: permissionsRole } = useRole(permissionsRoleId);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (editingRole) {
      setForm({
        name: editingRole.name,
        slug: editingRole.slug,
        description: editingRole.description || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [editingRole]);

  useEffect(() => {
    if (permissionsRole?.permissions) {
      setSelectedPermissions(new Set(permissionsRole.permissions));
    }
  }, [permissionsRole]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (role: AdminRole) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingRole(null);
      setForm(emptyForm);
    }
  };

  const openPermissionsModal = (roleId: string) => {
    setPermissionsRoleId(roleId);
  };

  const closePermissionsModal = () => {
    if (!updatePermissionsMutation.isPending) {
      setPermissionsRoleId(null);
      setSelectedPermissions(new Set());
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) return;

    try {
      if (editingRole) {
        await updateMutation.mutateAsync({
          roleId: editingRole.roleId,
          data: {
            name: form.name.trim(),
            description: form.description.trim() || null,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: form.name.trim(),
          slug: form.slug.trim(),
          description: form.description.trim() || null,
        });
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save role:", error);
    }
  };

  const handleDelete = async (roleId: string) => {
    try {
      await deleteMutation.mutateAsync(roleId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Failed to delete role:", error);
    }
  };

  const togglePermission = (code: string) => {
    const newSet = new Set(selectedPermissions);
    if (newSet.has(code)) {
      newSet.delete(code);
    } else {
      newSet.add(code);
    }
    setSelectedPermissions(newSet);
  };

  const handleSavePermissions = async () => {
    if (!permissionsRoleId) return;
    try {
      await updatePermissionsMutation.mutateAsync({
        roleId: permissionsRoleId,
        permissions: Array.from(selectedPermissions),
      });
      closePermissionsModal();
    } catch (error) {
      console.error("Failed to update permissions:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="page">
        <PageHeader
          label="Administration"
          title="Roles"
          description="Manage roles and their permissions."
        />
        <div className="card">
          <div className="card-b">
            <p className="meta" style={{ textAlign: "center", padding: "32px 0" }}>
              Loading...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        label="Administration"
        title="Roles"
        description="Manage roles and their permissions."
        actions={
          canCreate("roles_management") && (
            <Button variant="primary" onClick={openCreateModal}>
              <Icon name="plus" />
              Add Role
            </Button>
          )
        }
      />

      {roles && roles.length > 0 ? (
        <div className="grid g3">
          {roles.map((role) => (
            <div key={role.roleId} className="acard">
              <div className="top">
                <span
                  className="stat-i g"
                  style={{ width: 34, height: 34, borderRadius: 10 }}
                >
                  <Icon name="shield" />
                </span>
                <span className="tx">
                  <b style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {role.name}
                    {role.isSystem && <span className="b def">System</span>}
                  </b>
                  <span className="mono">{role.slug}</span>
                </span>
              </div>
              {role.description && (
                <p
                  style={{
                    fontSize: "12.5px",
                    color: "var(--ink-3)",
                    margin: 0,
                    lineHeight: 1.5,
                    minHeight: 38,
                  }}
                >
                  {role.description}
                </p>
              )}
              <div className="bot">
                {canEdit("roles_management") && (
                  <>
                    {(!role.isSystem || isSuperadmin) && (
                      <Button variant="secondary" size="sm" onClick={() => openEditModal(role)}>
                        <Icon name="edit" />
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPermissionsModal(role.roleId)}
                    >
                      Permissions
                    </Button>
                  </>
                )}
                {canDelete("roles_management") &&
                  !role.isSystem &&
                  (deleteConfirmId === role.roleId ? (
                    <div style={{ display: "flex", gap: 6, flex: 1 }}>
                      <span
                        style={{
                          fontSize: "11.5px",
                          color: "var(--err)",
                          fontWeight: 620,
                          alignSelf: "center",
                          flex: 1,
                        }}
                      >
                        Delete this role?
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(role.roleId)}
                        loading={deleteMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirmId(null)}
                        disabled={deleteMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(role.roleId)}
                    >
                      Delete
                    </Button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">
          <div className="empty-i">
            <Icon name="shield" />
          </div>
          <b>No roles found</b>
          {canCreate("roles_management") && (
            <Button variant="primary" onClick={openCreateModal}>
              Create your first role
            </Button>
          )}
        </div>
      )}

      {/* Role Modal */}
      {isModalOpen && (
        <div
          className="ov open"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="mod">
            <div className="mod-h">
              <div className="tx">
                <h3>{editingRole ? "Edit Role" : "Create Role"}</h3>
                <p>
                  {editingRole
                    ? "The slug is used in code and cannot be changed after create."
                    : "Add a new role."}
                </p>
              </div>
              <button className="icon-btn" onClick={closeModal} disabled={isSaving}>
                <Icon name="x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mod-b">
                <div className="fg">
                  <label className="fl">
                    Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className="inp"
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        name,
                        slug: editingRole ? prev.slug : generateSlug(name),
                      }));
                    }}
                    placeholder="Content Manager"
                    required
                    disabled={isSaving}
                  />
                </div>

                <div className="fg">
                  <label className="fl">
                    Slug <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className="inp mono"
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder="content_manager"
                    required
                    disabled={isSaving || !!editingRole}
                  />
                  <p className="hint">
                    Unique identifier, lowercase with underscores
                    {editingRole ? " · locked on edit" : ""}
                  </p>
                </div>

                <div className="fg" style={{ marginBottom: 0 }}>
                  <label className="fl">Description</label>
                  <textarea
                    className="ta"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Manages content and articles..."
                    rows={3}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="mod-f">
                <Button type="button" variant="ghost" onClick={closeModal} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={isSaving}>
                  {editingRole ? "Save Changes" : "Create Role"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permissionsRoleId && permissionsRole && (
        <div
          className="ov open"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePermissionsModal();
          }}
        >
          <div className="mod lg">
            <div className="mod-h">
              <div className="tx">
                <h3>Permissions for {permissionsRole.name}</h3>
                <p>Select the permissions this role should have.</p>
              </div>
              <button
                className="icon-btn"
                onClick={closePermissionsModal}
                disabled={updatePermissionsMutation.isPending}
              >
                <Icon name="x" />
              </button>
            </div>

            <div className="mod-b">
              {availablePermissions &&
                Object.entries(availablePermissions).map(([resource, actions], idx, arr) => {
                  const selectedCount = actions.filter((a) =>
                    selectedPermissions.has(a.code)
                  ).length;
                  return (
                    <div
                      key={resource}
                      className="pgroup"
                      style={idx === arr.length - 1 ? { marginBottom: 0 } : undefined}
                    >
                      <div className="pgroup-h">
                        <b>{RESOURCE_LABELS[resource] || resource}</b>
                        <span className="c">
                          {selectedCount} / {actions.length}
                        </span>
                      </div>
                      <div className="pgroup-b">
                        {actions.map((action) => {
                          const isSelected = selectedPermissions.has(action.code);
                          const isDisabled = permissionsRole.isSystem && !isSuperadmin;
                          return (
                            <button
                              key={action.code}
                              type="button"
                              className={`perm${isSelected ? " on" : ""}`}
                              onClick={() => !isDisabled && togglePermission(action.code)}
                              disabled={isDisabled}
                              title={action.description}
                            >
                              <Icon name="check" />
                              {action.action}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="mod-f">
              <Button
                variant="ghost"
                onClick={closePermissionsModal}
                disabled={updatePermissionsMutation.isPending}
              >
                Cancel
              </Button>
              {(!permissionsRole.isSystem || isSuperadmin) && (
                <Button
                  variant="primary"
                  onClick={handleSavePermissions}
                  loading={updatePermissionsMutation.isPending}
                >
                  Save Permissions
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
